import { removeTrailingSlash } from './removeTrailingSlash';

/*
 * Преобразует путь до ассета в корректный публичный URL для SSR и CSR.
 *
 * Логика:
 * - если путь абсолютный (http/https, data/blob или //) → возвращается как есть;
 * - общий SSR-путь "/static/assets/" направляется в staticBase текущего приложения;
 * - остальные готовые "/static/" URL сохраняются;
 * - локальный dev-ассет направляется в бандл текущего приложения из staticBase;
 * - если путь относительный (server import вида "assets/...") →
 *   префиксируется базовым staticBase (например, "/static/mobile/").
 *
 * Это позволяет синхронизировать значения `src` для картинок
 * между серверным рендерингом (SSR) и клиентом (CSR) и избежать
 * ошибок гидрации вида "Prop `src` did not match".
 */
export function buildAssetUrl(staticBase: string, url: string): string {
    // SSR runtime общий, а клиентские ассеты stores/error лежат в отдельных бандлах.
    if (url.startsWith('/static/assets/') && staticBase) {
        return `${removeTrailingSlash(staticBase)}/${url.slice('/static/'.length)}`;
    }

    /** Проверяем абсолютные и встроенные URL */
    if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:') || url.startsWith('blob:')) {
        return url;
    }

    /** Проверяем protocol-relative URL (//betastatic.yastatic.net/s3/yastore-static/...) */
    if (isProtocolRelativeUrl(url)) {
        return url;
    }

    /** Проверяем пути, которые уже начинаются с /static/ (локальная сборка) */
    if (url.startsWith('/static/')) {
        return url;
    }

    // SSR и клиент импортируют один ассет из разных dev-бандлов.
    // Публичный URL должен указывать на клиентский бандл текущего приложения.
    if (url.startsWith('/.dev/static/')) {
        if (!staticBase.startsWith('/.dev/static/')) {
            return url;
        }

        const assetPath = url.replace(/^\/\.dev\/static\/[^/]+\//, '');

        return `${removeTrailingSlash(staticBase)}/${assetPath}`;
    }

    /**
     * Для относительных путей добавляем staticBase
     * Убираем завершающий слэш у staticBase и ведущие слэши у url
     */
    const cleanStaticBase = removeTrailingSlash(staticBase);
    const cleanUrl = url.replace(/^\/+/, '');

    return `${cleanStaticBase}/${cleanUrl}`;
}

export function isProtocolRelativeUrl(input: string): boolean {
    /** Опционально «подчистим» невидимые пробелы по краям */
    const url = input.trim();

    /**
     * Должно начинаться ровно с двух слэшей и третий символ не должен быть слэшем.
     * Это исключает "///..." (которое трактуется как путь, а не хост).
     */
    return url.startsWith('//') && url.length > 2 && url[2] !== '/';
}
